import { useMemo } from "react";

import { formatTimestamp } from "../../../../common/utils/timestampUtils";
import { useBasicEventTableColumns } from "../../hooks/useBasicEventTableColumns";
import { RECORDING_PAGE_SIZE } from "../../utils/constants";
import EventTable, { ExpandableData } from "./EventTable";
import { EventViewModel } from "../../models/eventViewModel";
import { RecordingViewModel } from "../../models/recordingViewModel";
import { useGetRecordingsByEvent } from "../../hooks/recordings/useGetRecordingsByEvent";

interface BasicEventTableProps {
  event: EventViewModel;
  newestRecording?: RecordingViewModel;
}

const BasicEventTable = ({ event, newestRecording }: BasicEventTableProps) => {
  const { columns } = useBasicEventTableColumns(newestRecording);

  const expandableData: ExpandableData[] = useMemo(
    () => [
      {
        label: "DEVICE ID",
        value: newestRecording?.rawEventInfo.data.camera_id ?? "",
      },
    ],
    [newestRecording]
  );

  const { recordings, fetchNextPage, lastEvaluatedKey, loading } =
    useGetRecordingsByEvent({
      customerId: event.customerId.slice(2),
      eventTimestamp: event.timestamp,
      pageSize: RECORDING_PAGE_SIZE,
    });

  // Sort recordings oldest→newest (chronological order)
  const sortedRecordings = useMemo(
    () =>
      [...recordings].sort(
        (a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      ),
    [recordings]
  );

  const newestRecordingTimestamp =
    newestRecording?.timestamp && formatTimestamp(newestRecording.timestamp);

  return (
    <EventTable
      columns={columns}
      event={event}
      expandableData={expandableData}
      records={sortedRecordings}
      latestRecordingTimestamp={newestRecordingTimestamp}
      fetchNextPage={fetchNextPage}
      shouldLoadMore={!!lastEvaluatedKey}
      recordingsCount={event.recordingsCount}
      recordingsLoading={loading}
    />
  );
};

export default BasicEventTable;
